import { Skeleton } from "@bednbody/ui/components/skeleton";
const STATUS_STYLES: Record<string, string> = {
  pending: "bg-warning/15 text-warning",
  awaiting_rider: "bg-warning/15 text-warning",
  dispatched: "bg-info/15 text-info",
  en_route_to_client: "bg-info/15 text-info",
  arrived: "bg-info/15 text-info",
  in_session: "bg-primary/15 text-primary-dark",
  completed: "bg-success/15 text-success",
  cancelled: "bg-destructive/15 text-destructive",
};

interface Booking {
  id: string;
  ref: string;
  status: string;
  scheduled_at: string;
  booking_group_id?: string | null;
  service_variants: { duration_minutes: number; services: { name: string } | null } | null;
}

import { bookingGroupStatus, byRowId, groupRowsByBooking } from "@bednbody/logic";
import { ReadFailedCard } from "@bednbody/ui/components/read-failed-card";
interface RecentBookingsListProps {
  /** A failed read is not a quiet day — see ReadFailedCard. */
  failed?: boolean;
  bookings: Booking[];
  isLoading: boolean;
  /** Rows shown before the "+N more" line; full table lives on /bookings. */
  maxRows?: number;
}

/** Dashboard-compact booking rows (ref, time, service, status). */
export function RecentBookingsList({
  bookings,
  isLoading,
  failed = false,
  maxRows = 5,
}: RecentBookingsListProps) {
  if (isLoading) {
    return (
      <div className="space-y-2">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-11 motion-reduce:animate-none" />
        ))}
      </div>
    );
  }

  if (failed) {
    return (
      <ReadFailedCard message="Today's bookings could not be read — this is not a quiet day." />
    );
  }

  if (bookings.length === 0) {
    return <p className="text-sm text-muted-foreground">No bookings yet today.</p>;
  }

  // One row per BOOKING — a booking for four is four rows (0005).
  const grouped = groupRowsByBooking(bookings, (b) => b.booking_group_id, byRowId);
  const visible = grouped.slice(0, maxRows);
  const remainder = grouped.length - visible.length;

  return (
    <div className="divide-y divide-border">
      {visible.map((members) => {
        const [b] = members;
        const status = bookingGroupStatus(members.map((m) => m.status));
        return (
          <div key={b.id} className="flex items-center gap-3 py-2">
            <span className="w-20 shrink-0 text-sm font-medium">{b.ref}</span>
            <span className="w-16 shrink-0 text-xs tabular-nums text-muted-foreground">
              {new Date(b.scheduled_at).toLocaleTimeString("en-PH", {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
            <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
              {members.length > 1
                ? `Group · ${members.length} clients`
                : `${b.service_variants?.services?.name ?? "-"}${
                    b.service_variants?.duration_minutes
                      ? ` · ${b.service_variants.duration_minutes}min`
                      : ""
                  }`}
            </span>
            <span
              className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium capitalize ${
                STATUS_STYLES[status] ?? "bg-border text-muted-foreground"
              }`}
            >
              {status.replace(/_/g, " ")}
            </span>
          </div>
        );
      })}
      {remainder > 0 ? (
        <p className="pt-2 text-xs text-muted-foreground">+{remainder} more</p>
      ) : null}
    </div>
  );
}
